import { useToast } from '@chakra-ui/react'
import { endOfDay, format, startOfDay, subDays } from 'date-fns'
import {
  createContext,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'
import { useImmer } from 'use-immer'

import {
  formatDateBR,
  getRowStatus,
  parseDate,
  toPercentage
} from '@component/Dashboard/PurchaseSchedule/utils'

import { getVisualizaTodosCompradores } from '@helper/auth/getUserTokenClaims'
import { exportToCSV } from '@helper/export/csvUtils'
import {
  IControleFornecedorFiltro,
  IControleFornecedorItem,
  IPagination,
  getControleFornecedor,
  getControleFornecedorHistorico
} from '@service/controlefornecedor'

import {
  IControleFornecedorPagedResponse,
  IHistoricoControleFornecedor,
  IPurchaseScheduleContext,
  IPurchaseScheduleData,
  IStatusCounts,
  IStatusPercentages
} from '@base/@types/providers/Dashboard/PurchaseScheduleProvider'

// Linhas por requisição e limite de segurança de páginas. O ideal seria um
// endpoint agregado no backend (contagens/resumo) em vez de baixar tudo.
// Tamanho de página já validado com o backend (até 10.000 registros)
const ROWS_PER_PAGE = 50
const MAX_PAGES = 200

const itemKey = (item: IControleFornecedorItem) =>
  [
    item.empresaId,
    item.lojaId,
    item.fornecedorId,
    item.categoriaCompactaId,
    item.compradorEmail
  ].join('|')

const DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss"

// Período padrão do filtro: últimos 30 dias (yyyy-MM-dd, horário local)
const getDefaultPeriod = () => {
  const today = new Date()
  return {
    initialDate: format(subDays(today, 30), 'yyyy-MM-dd'),
    finalDate: format(today, 'yyyy-MM-dd')
  }
}

const getErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.message || fallback

const isSemDados = (error: any): boolean =>
  error?.response?.status === 400 &&
  /nenhum dado encontrado/i.test(String(error?.response?.data?.message ?? ''))

const PurchaseScheduleProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [loadWarning, setLoadWarning] = useState<string | null>(null)
  const [filters, setFilters] = useState(false)
  const [params, setParams] = useState<any>(getDefaultPeriod)
  const [historico, setHistorico] = useState<IHistoricoControleFornecedor[]>([])
  const [loadingHistorico, setLoadingHistorico] = useState(false)

  // Identificador da busca corrente: respostas de buscas anteriores (ou
  // canceladas) são descartadas e o laço de páginas é interrompido
  const requestIdRef = useRef(0)
  const historicoRequestIdRef = useRef(0)

  const [data, updateData] = useImmer<IPurchaseScheduleData[]>([])

  const statusCounts = useMemo((): IStatusCounts => {
    return data.reduce(
      (acc, item) => {
        acc[getRowStatus(item)]++
        acc.total++
        return acc
      },
      { emAtraso: 0, pendente: 0, noPrazo: 0, total: 0 }
    )
  }, [data])

  const statusPercentages = useMemo(
    (): IStatusPercentages => ({
      emAtraso: toPercentage(statusCounts.emAtraso, statusCounts.total),
      pendente: toPercentage(statusCounts.pendente, statusCounts.total),
      noPrazo: toPercentage(statusCounts.noPrazo, statusCounts.total)
    }),
    [statusCounts]
  )

  // Filtro Compradores só para Administrador/Analista de Planejamento; os
  // demais perfis veem o recorte que a API aplica (carteira própria ou claims)
  const visualizaTodosCompradores = useMemo(
    () => getVisualizaTodosCompradores(),
    []
  )

  const filtersList = useMemo(() => {
    const list = [
      {
        id: 'companies',
        label: 'Empresas',
        type: 'select-pagination',
        filterId: 'companies',
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'centers',
        label: 'Lojas',
        type: 'select-pagination',
        filterId: 'centers',
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'providersForPurchase',
        label: 'Fornecedores',
        type: 'select-pagination',
        filterId: 'providersForPurchase',
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'compactCategories',
        label: 'Categorias',
        type: 'select-pagination',
        filterId: 'compactCategories',
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'emAtraso',
        label: 'Em Atraso',
        type: 'checkbox',
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'pendencia',
        label: 'Pendência',
        type: 'checkbox',
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'compradores',
        label: 'Compradores',
        type: 'select-pagination',
        filterId: 'compradores',
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'periodo',
        label: 'Período',
        type: 'date-input',
        isRequired: false,
        isDisabled: false
      }
    ]
    return visualizaTodosCompradores
      ? list
      : list.filter(filter => filter.id !== 'compradores')
  }, [visualizaTodosCompradores])

  const cancel = useCallback(() => {
    requestIdRef.current++
    historicoRequestIdRef.current++
    setLoading(false)
    setLoadingHistorico(false)
  }, [])

  const getData = useCallback(async () => {
    const requestId = ++requestIdRef.current
    const isStale = () => requestId !== requestIdRef.current

    setLoading(true)
    setLoadWarning(null)

    const defaults = getDefaultPeriod()
    const toDate = (value: string, fallback: string) =>
      parseDate(value) || parseDate(fallback)

    // Filtro baseado nos parâmetros selecionados
    const filtro: IControleFornecedorFiltro = {
      empresasIds: params.companies?.map((item: any) => item.value) || [],
      lojasIds: params.centers?.map((item: any) => item.value) || [],
      fornecedoresIds:
        params.providersForPurchase?.map((item: any) => item.value) || [],
      categoriasCompactasIds:
        params.compactCategories?.map((item: any) => item.value) || [],
      compradores: visualizaTodosCompradores
        ? params.compradores?.map((item: any) => item.value) || []
        : [],
      emAtraso: params.emAtraso || null,
      pendencia: params.pendencia || null,
      // Início/fim do dia no horário local, no mesmo formato do padrão
      dataAtualizacaoInicio: format(
        startOfDay(toDate(params.initialDate, defaults.initialDate)),
        DATE_TIME_FORMAT
      ),
      dataAtualizacaoFim: format(
        endOfDay(toDate(params.finalDate, defaults.finalDate)),
        DATE_TIME_FORMAT
      )
    }

    const allItems: IControleFornecedorItem[] = []
    let warning: string | null = null

    try {
      let previousFirstKey: string | null = null
      for (let page = 1; page <= MAX_PAGES; page++) {
        const pagination: IPagination = {
          actualPage: page,
          rows: ROWS_PER_PAGE
        }

        let res: IControleFornecedorPagedResponse
        try {
          const response = await getControleFornecedor(filtro, pagination)
          res = response.data as IControleFornecedorPagedResponse
        } catch (err) {
          if (page === 1) throw err
          warning = `Falha ao carregar a página ${page}: ${getErrorMessage(
            err,
            'erro desconhecido'
          )}`
          break
        }

        // Busca cancelada ou substituída por outra: descarta e para o laço
        if (isStale()) return

        if (!res?.success) {
          const message = res?.message || 'Erro ao buscar dados'
          if (page === 1) throw new Error(message)
          warning = `Falha ao carregar a página ${page}: ${message}`
          break
        }

        const pageItems = Array.isArray(res.data) ? res.data : []

        // Backend que ignora a paginação devolve a mesma página de novo
        if (page > 1 && pageItems.length > 0 && previousFirstKey !== null) {
          if (itemKey(pageItems[0]) === previousFirstKey) break
        }
        previousFirstKey = pageItems.length > 0 ? itemKey(pageItems[0]) : null

        allItems.push(...pageItems)

        const totalPages = res.pagination?.totalPages
        const pageSize = res.pagination?.rows || ROWS_PER_PAGE
        // Sem paginação na resposta: página incompleta (ou backend que
        // ignorou o tamanho e devolveu tudo) encerra a busca
        const isLastPage =
          typeof totalPages === 'number'
            ? page >= totalPages
            : pageItems.length === 0 || pageItems.length !== pageSize
        if (isLastPage) break

        if (page === MAX_PAGES) {
          warning = `Resultado limitado a ${allItems.length.toLocaleString(
            'pt-BR'
          )} registros. Refine os filtros para ver todos.`
        }
      }

      if (isStale()) return

      const transformedData: IPurchaseScheduleData[] = allItems.map(item => ({
        compradorEmail: item.compradorEmail,
        empresaId: item.empresaId,
        empresaNome: item.empresaNome,
        lojaId: item.lojaId,
        lojaNome: item.lojaNome,
        fornecedorId: item.fornecedorId,
        fornecedorNome: item.fornecedorNome,
        categoriaCompactaId: item.categoriaCompactaId,
        categoriaCompactaNome: item.categoriaCompactaNome,
        frequencia: item.frequencia,
        proximaCompraPrevista: item.proximaCompraPrevista,
        emAtraso: item.emAtraso,
        pendencia: item.pendencia,
        dataAtualizacao: item.dataAtualizacao
      }))

      updateData(transformedData)

      if (warning) {
        setLoadWarning(`Dados parciais. ${warning}`)
        toast({
          title: 'Dados parciais',
          description: `${transformedData.length.toLocaleString(
            'pt-BR'
          )} registros carregados. ${warning}`,
          status: 'warning',
          duration: 6000,
          isClosable: true
        })
      } else {
        toast({
          title: 'Dados carregados com sucesso!',
          description: `${transformedData.length.toLocaleString('pt-BR')} ${
            transformedData.length === 1
              ? 'registro encontrado'
              : 'registros encontrados'
          }`,
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      }
    } catch (error) {
      if (isStale()) return

      // Não mantém dados do filtro anterior como se fossem do atual
      updateData([])
      const message = getErrorMessage(error, 'Tente novamente mais tarde')
      setLoadWarning(`Erro ao carregar dados: ${message}`)
      toast({
        title: 'Erro ao carregar dados',
        description: message,
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    } finally {
      if (!isStale()) setLoading(false)
    }
  }, [updateData, toast, params, visualizaTodosCompradores])

  const exportData = useCallback(() => {
    try {
      const yesNo = (value: boolean | null | undefined) =>
        value == null ? '' : value ? 'Sim' : 'Não'

      exportToCSV({
        filename: `controle-fornecedores-${format(
          new Date(),
          'yyyy-MM-dd'
        )}.csv`,
        headers: [
          'Comprador Email',
          'Empresa ID',
          'Empresa Nome',
          'Loja ID',
          'Loja Nome',
          'Fornecedor ID',
          'Fornecedor Nome',
          'Categoria Compacta ID',
          'Categoria Compacta Nome',
          'Frequência',
          'Próxima Compra Prevista',
          'Em Atraso',
          'Pendência',
          'Data Atualização'
        ],
        data: data.map(row => ({
          ...row,
          proximaCompraPrevista: formatDateBR(
            row.proximaCompraPrevista,
            'dd/MM/yyyy',
            ''
          ),
          dataAtualizacao: formatDateBR(
            row.dataAtualizacao,
            'dd/MM/yyyy HH:mm',
            ''
          ),
          emAtraso: yesNo(row.emAtraso),
          pendencia: yesNo(row.pendencia)
        })),
        fieldMapping: [
          'compradorEmail',
          'empresaId',
          'empresaNome',
          'lojaId',
          'lojaNome',
          'fornecedorId',
          'fornecedorNome',
          'categoriaCompactaId',
          'categoriaCompactaNome',
          'frequencia',
          'proximaCompraPrevista',
          'emAtraso',
          'pendencia',
          'dataAtualizacao'
        ]
      })

      toast({
        title: 'Dados exportados com sucesso!',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error) {
      toast({
        title: 'Erro ao exportar dados',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }, [data, toast])

  const getHistorico = useCallback(
    async (empresasIds: number[]) => {
      const requestId = ++historicoRequestIdRef.current
      const isStale = () => requestId !== historicoRequestIdRef.current

      const ids = Array.from(
        new Set(empresasIds.map(Number).filter(id => id > 0))
      )
      if (ids.length === 0) {
        setHistorico([])
        return
      }

      setLoadingHistorico(true)
      try {
        const responses = await Promise.all(
          ids.map(id =>
            getControleFornecedorHistorico(id).catch(err => {
              // Empresa sem histórico: 400 "Nenhum dado encontrado" = lista vazia
              if (isSemDados(err)) return { data: { data: [] } }
              throw err
            })
          )
        )
        if (isStale()) return

        const items: IHistoricoControleFornecedor[] = responses.flatMap(
          ({ data: res }: any) => (Array.isArray(res?.data) ? res.data : [])
        )
        items.sort((a, b) =>
          String(b.dataAcao ?? '').localeCompare(String(a.dataAcao ?? ''))
        )
        setHistorico(items)
      } catch (err) {
        if (isStale()) return
        setHistorico([])
        toast({
          title: 'Erro ao buscar histórico',
          description: getErrorMessage(err, 'Tente novamente mais tarde'),
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      } finally {
        if (!isStale()) setLoadingHistorico(false)
      }
    },
    [toast]
  )

  const handleUpdateParams = useCallback(
    ({ name, value }: { name: string; value: any }) => {
      setParams((prev: any) => {
        // Limpar o campo de data volta ao padrão (nunca envia data vazia)
        if ((name === 'initialDate' || name === 'finalDate') && !value) {
          return { ...prev, [name]: getDefaultPeriod()[name] }
        }
        return { ...prev, [name]: value }
      })
    },
    []
  )

  const clearAllFilters = useCallback(() => {
    setParams(getDefaultPeriod())
  }, [])

  const value = useMemo(
    () => ({
      loading,
      loadWarning,
      data,
      statusCounts,
      statusPercentages,
      getData,
      cancel,
      exportData,
      filters,
      setFilters,
      params,
      filtersList,
      handleUpdateParams,
      clearAllFilters,
      historico,
      loadingHistorico,
      getHistorico
    }),
    [
      loading,
      loadWarning,
      data,
      statusCounts,
      statusPercentages,
      getData,
      cancel,
      exportData,
      filters,
      params,
      filtersList,
      handleUpdateParams,
      clearAllFilters,
      historico,
      loadingHistorico,
      getHistorico
    ]
  )

  return (
    <PurchaseScheduleContext.Provider value={value}>
      {children}
    </PurchaseScheduleContext.Provider>
  )
}

export default PurchaseScheduleProvider
export const PurchaseScheduleContext = createContext<IPurchaseScheduleContext>(
  {} as IPurchaseScheduleContext
)
